class CartRewardBar extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = window.FoxTheme.settings.moneyFormat;
    this.progress = this.querySelector('[data-progress]');
    this.messages = Array.from(this.querySelectorAll('[data-message-index]'));
    this.completedMessage = this.querySelector('[data-message-complete]');

    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;

    this.milestones = Array.from(this.querySelectorAll('[data-milestone]')).map((element) => {
      // Amounts flagged for rate adjustment come from a theme setting held in the shop
      // currency, while cart prices arrive in the presentment currency.
      const base = parseInt(element.dataset.amount, 10) || 0;
      const amount = element.dataset.rateAdjust === 'true' ? Math.round(base * rate) : base;

      if (amount !== base) {
        const label = element.querySelector('[data-milestone-amount]');
        if (label) label.textContent = FoxTheme.Currency.formatMoney(amount, this.moneyFormat);
      }

      return {
        element,
        amount,
        position: parseInt(element.dataset.position, 10) || 0,
        basis: element.dataset.basis,
        // The gift milestone doubles as a way into the gift picker, when there is one —
        // the whole milestone is the button, so it carries the trigger itself.
        action: element.matches('[data-gwp-open]') ? element : element.querySelector('[data-gwp-open]'),
      };
    });

    this.update({
      gift: parseInt(this.dataset.giftTotal, 10) || 0,
      order: parseInt(this.dataset.orderTotal, 10) || 0,
    });

    this.onCartUpdated = (event) => this.refresh(event.detail.cart);
    document.addEventListener('cart:updated', this.onCartUpdated);

    this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cart) this.refresh(event.cart);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
    if (this.unsubscribe) this.unsubscribe();
  }

  /**
   * Each milestone is measured the way its reward is granted, mirroring the Liquid
   * render. The gift counts the selling price before any discount, without the gift line
   * itself — the GWP gate adds that line once the threshold is reached, and counting it
   * would make the progress jump. Free shipping counts the cart total after discounts,
   * as Shopify's shipping rates do.
   */
  refresh(cart) {
    const gift = (cart.items || [])
      .filter((item) => !(item.properties && item.properties._gwp === 'true'))
      .reduce((sum, item) => sum + item.original_line_price, 0);

    this.update({ gift, order: cart.total_price || 0 });
  }

  update(totals) {
    if (!this.milestones.length) return;

    this.classList.toggle('hidden', totals.gift <= 0);

    const totalFor = (milestone) => (milestone.basis === 'gift' ? totals.gift : totals.order);
    const nextIndex = this.milestones.findIndex((milestone) => totalFor(milestone) < milestone.amount);
    const isComplete = nextIndex === -1;

    this.milestones.forEach((milestone) => {
      const reached = totalFor(milestone) >= milestone.amount;
      milestone.element.classList.toggle('cart-reward-bar__milestone--reached', reached);
      // Nothing to choose from before the gift is earned.
      if (milestone.action) milestone.action.disabled = !reached;
    });

    this.messages.forEach((message, index) => {
      message.hidden = isComplete || index !== nextIndex;
    });
    this.completedMessage.hidden = !isComplete;
    this.classList.toggle('cart-reward-bar--complete', isComplete);

    if (isComplete) {
      this.setPercent(100);
      return;
    }

    const next = this.milestones[nextIndex];
    const previous = nextIndex === 0 ? { amount: 0, position: 0 } : this.milestones[nextIndex - 1];
    const total = totalFor(next);
    const span = next.amount - previous.amount;
    const ratio = span > 0 ? (total - previous.amount) / span : 0;

    const remaining = this.messages[nextIndex].querySelector('[data-left-to-spend]');
    if (remaining) {
      remaining.innerHTML = FoxTheme.Currency.formatMoney(next.amount - total, this.moneyFormat);
    }

    this.setPercent(previous.position + (next.position - previous.position) * Math.min(Math.max(ratio, 0), 1));
  }

  setPercent(percent) {
    this.progress.style.setProperty('--percent', `${percent}%`);
  }
}
customElements.define('cart-reward-bar', CartRewardBar);
